import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
    Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";

export const COLORS = [
    { key: "blue", solid: "#3B82F6", soft: "#EFF6FF", text: "#1D4ED8" },
    { key: "emerald", solid: "#10B981", soft: "#ECFDF5", text: "#047857" },
    { key: "indigo", solid: "#6366F1", soft: "#EEF2FF", text: "#4338CA" },
    { key: "amber", solid: "#F59E0B", soft: "#FFFBEB", text: "#B45309" },
    { key: "rose", solid: "#F43F5E", soft: "#FFF1F2", text: "#BE123C" },
    { key: "violet", solid: "#8B5CF6", soft: "#F5F3FF", text: "#6D28D9" },
];

export const METRICS = [
    { key: "bound_premium", label: "Bound premium ($)", isMoney: true },
    { key: "estimated_premium", label: "Estimated premium ($)", isMoney: true },
    { key: "submissions_count", label: "Submissions count", isMoney: false },
    { key: "bound_count", label: "Bound submissions count", isMoney: false },
    { key: "new_active_brokers", label: "New active brokers", isMoney: false },
    { key: "new_brokers", label: "New target brokers added", isMoney: false },
    { key: "activities_logged", label: "Activities logged", isMoney: false },
    { key: "manual", label: "Manual (track yourself)", isMoney: false },
];

export default function GoGetFormDialog({ open, onOpenChange, goGet, onSuccess }) {
    const isEdit = !!goGet;
    const today = new Date();
    const yearStart = `${today.getFullYear()}-01-01`;
    const yearEnd = `${today.getFullYear()}-12-31`;

    const [form, setForm] = useState({});
    const [saving, setSaving] = useState(false);

    const reset = () => {
        if (goGet) {
            setForm({ ...goGet });
        } else {
            setForm({
                name: "",
                description: "",
                color: "blue",
                metric: "bound_premium",
                target: 0,
                manual_value: 0,
                period_start: yearStart,
                period_end: yearEnd,
                notes: "",
            });
        }
    };

    if (open && Object.keys(form).length === 0) reset();

    const handleOpenChange = (o) => {
        if (!o) setForm({});
        onOpenChange(o);
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.metric) {
            toast.error("Name and metric are required");
            return;
        }
        if (parseFloat(form.target) <= 0) {
            toast.error("Target must be greater than 0");
            return;
        }
        setSaving(true);
        try {
            const payload = {
                name: form.name,
                description: form.description,
                color: form.color,
                metric: form.metric,
                target: parseFloat(form.target) || 0,
                manual_value: parseFloat(form.manual_value) || 0,
                period_start: form.period_start || yearStart,
                period_end: form.period_end || yearEnd,
                notes: form.notes,
            };
            if (isEdit) {
                await api.patch(`/go-gets/${goGet.id}`, payload);
                toast.success("Go Get updated");
            } else {
                await api.post("/go-gets", payload);
                toast.success(`"${form.name}" added`);
            }
            onSuccess?.();
            handleOpenChange(false);
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    const isManual = form.metric === "manual";

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto scrollbar-thin" data-testid="go-get-form-dialog">
                <DialogHeader>
                    <DialogTitle className="text-lg font-semibold text-ph-text">
                        {isEdit ? "Edit Go Get" : "New Go Get"}
                    </DialogTitle>
                    <DialogDescription className="text-sm text-ph-muted">
                        Set a goal you're working toward. Progress is tracked automatically — except for manual goals, where you update the value yourself.
                    </DialogDescription>
                </DialogHeader>

                <form onSubmit={submit} className="space-y-4 pt-2" data-testid="go-get-form">
                    <div>
                        <label className="text-xs font-medium text-ph-text mb-1.5 block">Label *</label>
                        <Input
                            required
                            placeholder="e.g., $5M Bound Premium 2026"
                            value={form.name || ""}
                            onChange={(e) => setForm({ ...form, name: e.target.value })}
                            data-testid="go-get-name-input"
                        />
                    </div>

                    <div>
                        <label className="text-xs font-medium text-ph-text mb-2 block">Color</label>
                        <div className="flex gap-2" data-testid="go-get-color-row">
                            {COLORS.map((c) => (
                                <button
                                    key={c.key}
                                    type="button"
                                    onClick={() => setForm({ ...form, color: c.key })}
                                    className={`w-8 h-8 rounded-full border-2 transition-all ${
                                        form.color === c.key ? "scale-110 border-ph-text" : "border-transparent hover:scale-105"
                                    }`}
                                    style={{ backgroundColor: c.solid }}
                                    title={c.key}
                                    data-testid={`go-get-color-${c.key}`}
                                />
                            ))}
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="text-xs font-medium text-ph-text mb-1.5 block">Metric *</label>
                            <Select value={form.metric || "bound_premium"} onValueChange={(v) => setForm({ ...form, metric: v })}>
                                <SelectTrigger data-testid="go-get-metric-trigger"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {METRICS.map((m) => <SelectItem key={m.key} value={m.key}>{m.label}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <label className="text-xs font-medium text-ph-text mb-1.5 block">Target *</label>
                            <Input
                                type="number"
                                required
                                value={form.target || 0}
                                onChange={(e) => setForm({ ...form, target: e.target.value })}
                                data-testid="go-get-target-input"
                            />
                        </div>
                        <div>
                            <label className="text-xs font-medium text-ph-text mb-1.5 block">Period start</label>
                            <Input type="date" value={form.period_start || yearStart} onChange={(e) => setForm({ ...form, period_start: e.target.value })} data-testid="go-get-period-start-input" />
                        </div>
                        <div>
                            <label className="text-xs font-medium text-ph-text mb-1.5 block">Period end</label>
                            <Input type="date" value={form.period_end || yearEnd} onChange={(e) => setForm({ ...form, period_end: e.target.value })} data-testid="go-get-period-end-input" />
                        </div>
                        {isManual && (
                            <div className="md:col-span-2">
                                <label className="text-xs font-medium text-ph-text mb-1.5 block">Current value (manual)</label>
                                <Input
                                    type="number"
                                    value={form.manual_value || 0}
                                    onChange={(e) => setForm({ ...form, manual_value: e.target.value })}
                                    data-testid="go-get-manual-input"
                                />
                                <p className="text-[11px] text-ph-mutedSoft mt-1">
                                    Update this number directly as you make progress.
                                </p>
                            </div>
                        )}
                        <div className="md:col-span-2">
                            <label className="text-xs font-medium text-ph-text mb-1.5 block">Description</label>
                            <Textarea
                                rows={2}
                                placeholder="Why does this goal matter?"
                                value={form.description || ""}
                                onChange={(e) => setForm({ ...form, description: e.target.value })}
                                data-testid="go-get-description-input"
                            />
                        </div>
                    </div>

                    <div className="flex justify-end gap-3 pt-3 border-t border-ph-borderSoft">
                        <Button type="button" variant="outline" onClick={() => handleOpenChange(false)} className="font-medium" data-testid="go-get-cancel-btn">
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            disabled={saving}
                            className="bg-ph-primary text-white font-medium hover:bg-ph-primaryHover"
                            data-testid="go-get-submit-btn"
                        >
                            {saving ? "Saving…" : isEdit ? "Save changes" : "Create Go Get"}
                        </Button>
                    </div>
                </form>
            </DialogContent>
        </Dialog>
    );
}
